import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { CheckCircle2, Ban, AlertTriangle, FileText, Building2, Clock, Wallet } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import { useAuth } from '../../hooks/useAuth';
import { ROLES } from '../../utils/roles';
import * as clinicsApi from '../../api/clinics.api';
import { errorMessage } from '../../api/client';
import { formatDate } from '../../utils/format';

const STATUS_FILTERS = [
  { value: 'PENDING',   label: 'Pending review' },
  { value: 'APPROVED',  label: 'Approved' },
  { value: 'REJECTED',  label: 'Rejected' },
  { value: 'SUSPENDED', label: 'Suspended' },
];

function StatusPill({ status }) {
  const map = {
    PENDING:   { tone: 'warning', icon: Clock,         label: 'Pending' },
    APPROVED:  { tone: 'success', icon: CheckCircle2,  label: 'Approved' },
    REJECTED:  { tone: 'danger',  icon: Ban,           label: 'Rejected' },
    SUSPENDED: { tone: 'danger',  icon: AlertTriangle, label: 'Suspended' },
  };
  const m = map[status] || map.PENDING;
  const Icon = m.icon;
  return (
    <span className={`badge ${m.tone}`}>
      <Icon size={12} /> {m.label}
    </span>
  );
}

function DocLink({ clinicId, kind, hasFile }) {
  if (!hasFile) return <span className="muted text-sm">— Missing —</span>;
  async function open() {
    try {
      const blob = await clinicsApi.fetchClinicDocumentBlob(clinicId, kind);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke later to free memory.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      toast.error(errorMessage(e));
    }
  }
  return (
    <button className="btn btn-outline" onClick={open}>
      <FileText size={14} /> View {kind.toUpperCase()}
    </button>
  );
}

function ClinicCard({ clinic, onAction, busy, canEditPayout }) {
  const cp = clinic.clinicProfile || {};
  const status = cp.approvalStatus || 'PENDING';
  const apcExpired = cp.apcExpiryDate && new Date(cp.apcExpiryDate) < new Date();
  const sharePct = Number(cp.clinicSharePct) || 0;
  const bankSet = Boolean(cp.payoutSettings?.bankCode && cp.payoutSettings?.accountNumber);

  const missingForApproval = [];
  if (!cp.mmcLicenseNumber)  missingForApproval.push('MMC #');
  if (!cp.apcCertNumber)     missingForApproval.push('APC #');
  if (!cp.apcExpiryDate)     missingForApproval.push('APC expiry');
  if (!cp.mmcLicenseDocKey)  missingForApproval.push('MMC doc');
  if (!cp.apcCertDocKey)     missingForApproval.push('APC doc');

  return (
    <div className="card" style={{ marginBottom: 'var(--s-3)' }}>
      <div className="row between" style={{ alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
        <div style={{ flex: '1 1 320px' }}>
          <div className="row" style={{ gap: 10, alignItems: 'center' }}>
            <Building2 size={18} />
            <span className="bold" style={{ fontSize: 16 }}>{cp.clinicName || clinic.name}</span>
            <StatusPill status={status} />
          </div>
          <div className="muted text-sm" style={{ marginTop: 4 }}>
            {clinic.email} · {clinic.phone || 'no phone'} · joined {formatDate(clinic.createdAt)}
          </div>

          <div className="grid grid-2" style={{ gap: 'var(--s-2) var(--s-4)', marginTop: 'var(--s-3)' }}>
            <div><span className="muted text-xs">MMC #</span><div>{cp.mmcLicenseNumber || <span className="muted">—</span>}</div></div>
            <div><span className="muted text-xs">APC #</span><div>{cp.apcCertNumber || <span className="muted">—</span>}</div></div>
            <div><span className="muted text-xs">APC expiry</span>
              <div>
                {cp.apcExpiryDate ? formatDate(cp.apcExpiryDate) : <span className="muted">—</span>}
                {apcExpired && <span className="badge danger" style={{ marginLeft: 8 }}>Expired</span>}
              </div>
            </div>
            <div><span className="muted text-xs">Prescribing doctor</span><div>{cp.prescribingDoctor || <span className="muted">—</span>}</div></div>
            <div><span className="muted text-xs">Address</span>
              <div>
                {(() => {
                  const parts = [cp.addressLine1, cp.city, cp.state, cp.postcode].filter(Boolean);
                  const addr = parts.join(', ');
                  if (!addr) return <span className="muted">—</span>;
                  // Client 2026-09-10: link the address to Google Maps
                  // so admin can visually verify the clinic location
                  // before approval.
                  const query = encodeURIComponent(
                    [cp.clinicName || clinic.name, addr].filter(Boolean).join(', ')
                  );
                  const mapsUrl = `https://maps.google.com/?q=${query}`;
                  return (
                    <>
                      {addr}{' '}
                      <a
                        href={mapsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm"
                        style={{ marginLeft: 4, color: 'var(--brand-700)', fontWeight: 600 }}
                      >
                        View on Maps ↗
                      </a>
                    </>
                  );
                })()}
              </div>
            </div>
            <div><span className="muted text-xs">Coordinates</span>
              <div>
                {cp.location?.coordinates
                  ? (
                    <>
                      {cp.location.coordinates[1].toFixed(4)}, {cp.location.coordinates[0].toFixed(4)}{' '}
                      <a
                        href={`https://maps.google.com/?q=${cp.location.coordinates[1]},${cp.location.coordinates[0]}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm"
                        style={{ marginLeft: 4, color: 'var(--brand-700)', fontWeight: 600 }}
                      >
                        Open pin ↗
                      </a>
                    </>
                  )
                  : <span className="muted">—</span>}
              </div>
            </div>
          </div>

          {status === 'REJECTED' && cp.rejectionReason && (
            <div className="notice danger" style={{ marginTop: 'var(--s-3)' }}>
              <span className="muted">Last rejection:</span> {cp.rejectionReason}
            </div>
          )}

          {status === 'PENDING' && missingForApproval.length > 0 && (
            <div className="notice warning" style={{ marginTop: 'var(--s-3)' }}>
              <span className="bold">Missing for approval:</span> {missingForApproval.join(', ')}
            </div>
          )}

          <div className="row" style={{ gap: 8, marginTop: 'var(--s-3)' }}>
            <DocLink clinicId={clinic._id} kind="mmc" hasFile={!!cp.mmcLicenseDocKey} />
            <DocLink clinicId={clinic._id} kind="apc" hasFile={!!cp.apcCertDocKey} />
          </div>
        </div>

        <div className="col" style={{ gap: 8, minWidth: 180 }}>
          {status !== 'APPROVED' && (
            <button
              className="btn btn-primary"
              disabled={busy || missingForApproval.length > 0 || apcExpired}
              onClick={() => onAction('approve', clinic)}
              title={missingForApproval.length ? `Missing: ${missingForApproval.join(', ')}` : apcExpired ? 'APC is expired' : ''}
            >
              <CheckCircle2 size={14} /> Approve
            </button>
          )}
          {status !== 'REJECTED' && (
            <button className="btn btn-outline" disabled={busy} onClick={() => onAction('reject', clinic)}>
              <Ban size={14} /> Reject
            </button>
          )}
          {status === 'APPROVED' && (
            <button className="btn btn-outline" disabled={busy} onClick={() => onAction('suspend', clinic)}>
              <AlertTriangle size={14} /> Suspend
            </button>
          )}
          {canEditPayout && (
            <Link className="btn btn-outline" to={`/admin/clinics/${clinic._id}/payout-settings`}>
              <Wallet size={14} /> Payout {sharePct}%
            </Link>
          )}
          {canEditPayout && (
            <div className="muted text-xs" style={{ textAlign: 'center' }}>
              {sharePct > 0
                ? `${sharePct}% commission per completed treatment`
                : 'Commission not set'}
              {' · '}
              {bankSet ? 'bank set' : 'bank missing'}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReasonModal({ open, action, clinic, onCancel, onConfirm, busy }) {
  const [reason, setReason] = useState('');
  useEffect(() => { if (open) setReason(''); }, [open]);

  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === 'Escape') onCancel?.(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  if (!open) return null;
  const verb = action === 'reject' ? 'Reject' : action === 'suspend' ? 'Suspend' : action;
  const disabled = busy || !reason.trim();

  return (
    <div className="modal-backdrop" onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}>
      <div className="modal" role="dialog" aria-modal="true">
        <div className="modal-head">
          <h3>{verb} {clinic?.clinicProfile?.clinicName || clinic?.name}</h3>
        </div>
        <div className="modal-body">
          <div className="muted text-sm" style={{ marginBottom: 12 }}>
            Tell the clinic what to fix. They'll see this in their dashboard and can re-submit.
          </div>
          <textarea
            rows={4}
            className="textarea"
            placeholder="e.g. APC document is unreadable — please re-upload a clear scan."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button
            className="btn btn-danger"
            disabled={disabled}
            onClick={() => onConfirm(reason.trim())}
          >
            {verb}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClinicApprovals() {
  const { user } = useAuth();
  const canEditPayout = [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(user?.role);
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [reasonModal, setReasonModal] = useState({ open: false, action: '', clinic: null });

  async function load() {
    setLoading(true);
    try {
      const list = await clinicsApi.listClinics({ status: statusFilter });
      setItems(list || []);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);

  async function handleAction(action, clinic) {
    if (action === 'approve') {
      if (!window.confirm(`Approve ${clinic.clinicProfile?.clinicName || clinic.name}? They'll be able to receive bookings immediately.`)) return;
      setBusy(true);
      try {
        await clinicsApi.approveClinic(clinic._id);
        toast.success('Clinic approved');
        await load();
      } catch (e) {
        toast.error(errorMessage(e));
      } finally {
        setBusy(false);
      }
      return;
    }
    // reject / suspend — collect reason
    setReasonModal({ open: true, action, clinic });
  }

  async function confirmReason(reason) {
    const { action, clinic } = reasonModal;
    setBusy(true);
    try {
      if (action === 'reject') {
        await clinicsApi.rejectClinic(clinic._id, reason);
        toast.success('Clinic rejected');
      } else if (action === 'suspend') {
        await clinicsApi.suspendClinic(clinic._id, reason);
        toast.success('Clinic suspended');
      }
      setReasonModal({ open: false, action: '', clinic: null });
      await load();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader
        title="Clinics & approvals"
        subtitle="All clinics in one place. Filter by status, open licence documents, approve or reject inline."
      />

      <div className="row" style={{ gap: 8, marginBottom: 'var(--s-4)', flexWrap: 'wrap' }}>
        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            className={statusFilter === f.value ? 'btn btn-primary' : 'btn btn-outline'}
            onClick={() => setStatusFilter(f.value)}
          >{f.label}</button>
        ))}
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : items.length === 0 ? (
        <EmptyState
          title={statusFilter === 'PENDING' ? 'Inbox zero — no clinics waiting' : `No ${statusFilter.toLowerCase()} clinics`}
          description={statusFilter === 'PENDING' ? 'New clinic applications will show up here.' : ''}
        />
      ) : (
        items.map((c) => (
          <ClinicCard
            key={c._id}
            clinic={c}
            onAction={handleAction}
            busy={busy}
            canEditPayout={canEditPayout}
          />
        ))
      )}

      <ReasonModal
        open={reasonModal.open}
        action={reasonModal.action}
        clinic={reasonModal.clinic}
        busy={busy}
        onCancel={() => setReasonModal({ open: false, action: '', clinic: null })}
        onConfirm={confirmReason}
      />
    </>
  );
}

export default ClinicApprovals;
